import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { autobind } from 'core-decorators';
import Draggable from 'react-draggable';
import CircularProgressbar from 'react-circular-progressbar';

import {
  MainContainer,
  VideoContainer,
  Controller,
  Play,
  Pause,
  ActionButtons,
  ActionButton,
  CircularProgressbarContainer,
} from './MediaComponents';

import embedActions from '../actions/embedActions';

import styles from '../styles';
import utils from '../utils';
import variables from '../../common/variables';

class MediaViewer extends React.PureComponent {
  constructor(props) {
    super(props);

    const { wh } = this.getPositioningValues();

    this.state = {
      started: false,
      v: 'bottom',
      h: 'left',
      x: 0,
      y: window.innerHeight - wh,
      dragging: false,
      progress: 0,
    };

    this.buttonSize = props.size === 'big' ? 50 : 30;
  }

  componentDidMount() {
    window.addEventListener('resize', this.onWindowResize);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.index !== nextProps.index) {
      this.resetPreview();
    }
  }

  componentDidUpdate(prevProps) {
    const indexChanged = this.props.index !== prevProps.index;
    const previousVideoVisible = prevProps.visibleVideo;
    const currentVideoVisible = this.props.visibleVideo;

    if (
      this.state.started &&
      currentVideoVisible &&
      (indexChanged || !previousVideoVisible)
    ) {
      this.playPreview(true);
    } else if (indexChanged || (previousVideoVisible && !currentVideoVisible)) {
      this.pausePreview();
    }

    if (indexChanged) {
      this.resetProgress();
    }

    if (prevProps.size !== this.props.size) {
      this.buttonSize = this.props.size === 'big' ? 50 : 30;
      this.repositionWithSize();
    }

    if (prevProps.ready !== this.props.ready) {
      if (this.props.auto) {
        this.playPreview(true);
      }
    }

    if (prevProps.auto !== this.props.auto) {
      if (this.props.auto) {
        this.playPreview(true);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  @autobind
  onWindowResize() {
    this.repositionWithSize();
  }

  @autobind
  onDragStart() {
    this.setState({ dragging: true });
  }

  @autobind
  onDragStop(e, position) {
    const { wh, topOffset } = this.getPositioningValues();
    this.setState({ dragging: false });
    if (position.x + wh / 2 < window.innerWidth / 2) {
      this.setState({ h: 'left', x: 0 });
    } else {
      this.setState({ h: 'right', x: window.innerWidth - wh });
    }
    if (position.y + wh / 2 < window.innerHeight / 2) {
      this.setState({ v: 'top', y: topOffset });
    } else {
      this.setState({ v: 'bottom', y: window.innerHeight - wh });
    }
  }

  @autobind
  onPreviewTimeUpdate() {
    this.setState({
      isPlaying: this.preview && this.preview.playing,
      progress: (this.preview.currentTime / this.preview.duration) * 100,
    });
  }

  @autobind
  onPreviewPause() {
    this.updatePlayingState();
  }

  @autobind
  onPreviewEnded() {
    this.updatePlayingState();
    this.props.onPreviewEnded();
  }

  @autobind
  getPositioningValues() {
    const wh = this.props.size === 'big' ? 260 : 160;
    let topOffset;

    if (this.props.controlsVisible) {
      topOffset = 38;
    } else {
      topOffset = 0;
    }

    return { wh, topOffset };
  }

  updatePlayingState() {
    this.setState({ isPlaying: this.preview && this.preview.playing });
  }

  @autobind
  toggleStartStop() {
    const hasMedia = !!this.props.slide.media;
    if (!hasMedia) {
      return;
    }
    if (this.state.isPlaying) {
      this.pausePreview();
    } else {
      this.playPreview();
    }
  }

  @autobind
  resetPreview() {
    this.setState({ isPlaying: false });
  }

  @autobind
  resetProgress() {
    this.setState({ progress: 0 });
  }

  @autobind
  playPreview(rewind = false) {
    if (!this.preview) {
      return;
    }
    if (!this.state.started) {
      this.setState({ started: true });
    }
    // Modern browsers receive a promise when calling play()
    // and must handle it properly
    // https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
    const playPromise = this.preview.play();
    if (playPromise) {
      playPromise
        .then(() => {
          if (rewind) {
            this.preview.currentTime = 0;
          }
          // Playback started
        })
        .catch(() => {
          // Playback was prevented
        });
    } else if (rewind) {
      this.preview.currentTime = 0;
    }
  }

  @autobind
  pausePreview() {
    if (!this.preview) {
      return;
    }
    this.preview.pause();
  }

  @autobind
  repositionWithSize() {
    const { wh, topOffset } = this.getPositioningValues();

    if (this.state.h === 'left') {
      this.setState({ x: 0 });
    } else {
      this.setState({ x: window.innerWidth - wh });
    }
    if (this.state.v === 'top') {
      this.setState({ y: topOffset });
    } else {
      this.setState({ y: window.innerHeight - wh });
    }
  }

  render() {
    if (!this.props.show) {
      return null;
    }

    const url = this.props.slide.media.remoteUrl;
    const urlWithoutExtension = url.substr(0, url.lastIndexOf('.'));

    const R = this.props.size === 'big' ? 100 : 50;

    const y1 = R;
    const x1 = utils.getXPositionMediaButton('left', R, y1);
    const dragButtonPosition = {};
    if (this.state.h === 'left') {
      dragButtonPosition.left = x1;
    } else {
      dragButtonPosition.right = x1;
    }

    const progressBarStyles = {
      path: { stroke: variables.colors.blue },
      trail: { stroke: variables.colors.white },
    };
    if (this.state.progress === 0) {
      progressBarStyles.path.transition = 'none';
    }
    return (
      <Draggable
        onDrag={this.onDragStart}
        onStop={this.onDragStop}
        handle=".button-handle"
        position={{
          x: this.state.x,
          y: this.state.y,
        }}
      >
        <MainContainer
          size={this.props.size}
          visible={
            this.props.visibleVideo &&
            !(this.props.slidesShowed && this.state.h === 'left')
          }
          dragging={this.state.dragging}
        >
          <CircularProgressbarContainer>
            <CircularProgressbar
              percentage={this.state.progress}
              strokeWidth={this.props.size === 'big' ? 2.5 : 5}
              textForPercentage=""
              styles={progressBarStyles}
            />
          </CircularProgressbarContainer>
          <VideoContainer>
            <video
              ref={el => {
                this.preview = el;
              }}
              src={`${urlWithoutExtension.replace(
                '/video/upload/',
                '/video/upload/vc_auto/',
              )}.mp4`}
              style={{ ...styles.mediaVideo }}
              playsInline
              onPause={this.onPreviewPause}
              onEnded={this.onPreviewEnded}
              onTimeUpdate={this.onPreviewTimeUpdate}
            />
            {!this.state.isPlaying && (
              <img
                src={`${urlWithoutExtension.replace(
                  '/video/upload/',
                  '/video/upload/so_0/',
                )}.jpg`}
                style={{ ...styles.mediaVideoPoster }}
                alt=""
              />
            )}
            <Controller initialOpacity={this.state.started ? 0 : 0.8}>
              {this.state.isPlaying ? (
                <Pause
                  onClick={() => {
                    this.pausePreview();
                  }}
                  size={this.props.size}
                />
              ) : (
                <Play
                  onClick={() => {
                    this.playPreview();
                  }}
                  size={this.props.size}
                />
              )}
            </Controller>
          </VideoContainer>
          <ActionButtons>
            <ActionButton
              className="button-handle"
              cursor="move"
              bottom={y1}
              size={this.buttonSize}
              {...dragButtonPosition}
            >
              <i className="icon-hand" />
              <span>Drag</span>
            </ActionButton>
          </ActionButtons>
        </MainContainer>
      </Draggable>
    );
  }
}

MediaViewer.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  index: PropTypes.number.isRequired,
  slide: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.objectOf(PropTypes.any).isRequired,
    media: PropTypes.objectOf(PropTypes.any),
  }).isRequired,
  show: PropTypes.bool.isRequired,
  size: PropTypes.string.isRequired,
  visibleVideo: PropTypes.bool.isRequired,
  controlsVisible: PropTypes.bool.isRequired,
  onPreviewEnded: PropTypes.func.isRequired,
  auto: PropTypes.bool.isRequired,
  ready: PropTypes.bool.isRequired,
  slidesShowed: PropTypes.bool.isRequired,
};

MediaViewer.defaultProps = {};

const mapStateToProps = state => ({
  visibleVideo: state.mediaViewerReducer.visibleVideo,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(embedActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(MediaViewer);
